
<template>
  <div class="preview-chart">
    <v-card rounded="lg" elevation="2" class="chart-body mt-2">
      <!-- Loading skeleton -->
      <v-skeleton-loader v-if="loading" type="image" class="rounded-lg" />

      <!-- Chart -->
      <BarChart
        v-else
        :chart-data="datacollection"
        :options="options"
        :height="340"
        downloadable
        :project-name="projectName"
        :method-name="methodName"
        :key="chartKey"
      />
    </v-card>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BarChart from './BarChart.vue'
import { useBarOptions } from '@/composables/useBarOptions'

const props = defineProps({
  // Single-series mode: a list of { id, name, quantity } rows.
  rows: { type: Array, default: () => [] },
  // Grouped mode: named series, each { label, rows: [{ id, name, quantity }] }.
  // When provided (>1 entry), a legend is shown and bars are drawn side by side.
  series: { type: Array, default: null },

  title: { type: String, required: true },
  unit: { type: String, default: '' },
  fractionDigits: { type: Number, default: 0 },
  stacked: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },

  // Download-caption context (forwarded to BarChart → chartExport)
  projectName: { type: String, default: '' },
  methodName: { type: String, default: '' },
})

// Energy-carrier color palette (shared with the ProjectDetails Stats* charts)
const colors = {
  1: '#F7BA0B',
  2: '#D4652F',
  3: '#D4652F',
  4: '#904684',
  5: '#7B5141',
  6: '#9C6B30',
  7: '#8D1D2C',
  10: '#EEC900',
  11: '#A02128',
  12: '#2E5978',
  13: '#48A43F',
}
const defaultColor = '#9EA0A1'

// Neutral series colors for grouped (before/after) mode, where a single
// per-carrier palette would be ambiguous across two datasets.
const seriesColors = ['#9EA0A1', '#2E5978']

const isGrouped = computed(() => Array.isArray(props.series) && props.series.length > 1)

const options = computed(() => {
  const base = useBarOptions(props.title, props.unit, props.fractionDigits).value
  const merged = { ...base }
  if (props.stacked) {
    merged.scales = {
      ...base.scales,
      x: { ...base.scales.x, stacked: true },
      y: { ...base.scales.y, stacked: true },
    }
  }
  if (isGrouped.value) {
    merged.plugins = {
      ...base.plugins,
      legend: { display: true, position: 'top' },
      // Value badges are too noisy across two datasets; rely on tooltips.
      datalabels: { ...base.plugins.datalabels, display: false },
    }
  }
  return merged
})

function barStyle(extra = {}) {
  return {
    borderWidth: 0,
    barPercentage: 0.7,
    borderRadius: 6,
    borderSkipped: false,
    maxBarThickness: 48,
    datalabels: { align: 'center', anchor: 'center' },
    ...extra,
  }
}

const datacollection = computed(() => {
  if (isGrouped.value) {
    // Union of carrier labels across series, keyed by id.
    const byId = new Map()
    for (const s of props.series) {
      for (const r of s.rows || []) {
        if (!byId.has(r.id)) byId.set(r.id, r.name)
      }
    }
    const ids = [...byId.keys()]
    const labels = ids.map((id) => byId.get(id))
    return {
      labels,
      datasets: props.series.map((s, i) => {
        const lookup = new Map((s.rows || []).map((r) => [r.id, Number(r.quantity)]))
        return {
          label: s.label,
          data: ids.map((id) => lookup.get(id) ?? 0),
          backgroundColor: seriesColors[i % seriesColors.length],
          borderColor: seriesColors[i % seriesColors.length],
          ...barStyle(),
        }
      }),
    }
  }

  const rows = props.rows || []
  if (!Array.isArray(rows) || rows.length === 0) {
    return { labels: [], datasets: [] }
  }
  const bgColors = rows.map((r) => colors[Number(r.id)] ?? defaultColor)
  return {
    labels: rows.map((r) => r.name),
    datasets: [
      {
        label: '',
        data: rows.map((r) => Number(r.quantity)),
        backgroundColor: bgColors,
        borderColor: bgColors,
        ...barStyle({ barPercentage: 0.5 }),
      },
    ],
  }
})

const chartKey = computed(() => {
  const d = datacollection.value
  return `${d.labels.length}-${d.datasets.length}-${d.datasets[0]?.data?.length || 0}`
})
</script>

<style scoped>
.preview-chart {
  /* Match the amortisation card: a definite, centered width. The definite
     width also gives the responsive canvas something to measure against;
     without it the card collapses and the chart renders at 0px (blank card). */
  width: 450px;
  max-width: 100%;
  margin-inline: auto;
}

.chart-body {
  box-sizing: border-box;
  width: 100%;
  padding: 12px;
  min-height: 360px;
}

:deep(canvas) {
  border-radius: 8px;
}
</style>
